import React, { useContext, useState } from 'react'
import { api, formatDayMonthTime } from '../../utils/api'
import { AuthContext } from '../../hooks/useAuth'
import { useFormAutosave } from '../../hooks/useFormAutosave'
import { CREATE_DEAL_DRAFT_VERSION, createDealDraftKey, toDraftState } from './createDealDraft'
import { DEAL_STAGES, parseItems, parseItemLine } from '../../utils/dealConstants'

// Listing service stages make a consignment, which operators no longer create
// (spec 20261019_op-quote-routing). The Worker refuses them too.
const LISTING_SERVICE_STAGES = ['609520315', '853396936'] // Listing Service Offered, Listing Service Listing

// Stages available for new deals
const NEW_DEAL_STAGES = DEAL_STAGES.filter(s => s.id && ![
  'closedwon', 'closedlost', '1616888039', // Exclude closed and duplicate stages
  ...LISTING_SERVICE_STAGES,
].includes(s.id))

/**
 * Why the typed prices can't be saved as they stand, or null if they can.
 *
 * Manual pricing is all-or-nothing, and every price needs both halves:
 *
 *  - Leaving one item unpriced hands the quote to the automatic pricing worker,
 *    which re-prices EVERY item on it and overwrites what was typed here. The
 *    numbers would vanish minutes after a green success message.
 *  - A price tier carries a buy and a sell price. A missing half would be
 *    stored as £0, which reads downstream as a real price rather than a gap.
 *
 * Pricing nothing at all is always fine — that is the ordinary quote, priced
 * automatically. The worker enforces the same two rules (operatorTypedPrices);
 * this is what turns a 500 into something an operator can act on.
 */
export function manualPriceError(validItems) {
  const priced = validItems.filter(item => Number(item.sp) > 0 || Number(item.bp) > 0)
  if (priced.length === 0) return null

  if (priced.length < validItems.length) {
    return `Prices cover ${priced.length} of ${validItems.length} items. Price them all, or clear the prices `
      + 'and let automatic pricing handle the quote — a part-priced quote gets re-priced and loses your numbers.'
  }

  const incomplete = validItems.findIndex(item => !(Number(item.sp) > 0 && Number(item.bp) > 0))
  if (incomplete !== -1) {
    const item = validItems[incomplete]
    return `Item ${incomplete + 1} (${item.brand} ${item.model}) needs both an SP and a BP.`
  }

  return null
}

/**
 * Why the OP answers or the warehouse's suggestions can't be saved, or null.
 *
 * Every OP quote except a part exchange is priced by the engine, so what the
 * warehouse types is a suggestion: any mix of blank and priced items is fine.
 * Two answers are required, and with the price agreed every item needs its BP,
 * because that BP IS the agreed price (spec 20261019_op-quote-routing).
 * A part exchange still uses manualPriceError.
 */
export function opFormError(validItems, { itemWithUs, priceAgreed, isPartExchange }) {
  if (isPartExchange) return null
  if (itemWithUs == null) return 'Is the item with us? Pick Yes or No.'
  if (priceAgreed == null) return 'Is the price agreed? Pick Yes or No.'

  for (const [n, item] of validItems.entries()) {
    const name = `Item ${n + 1} (${item.brand} ${item.model})`
    const typed = [item.sp, item.bp].filter(v => v !== '' && v != null)
    if (typed.some(v => !(Number(v) > 0))) return `${name}: prices must be more than £0.`
    if (priceAgreed && !(Number(item.bp) > 0)) {
      return `${name} needs a BP. The price is agreed, so the BP is the agreed price.`
    }
  }
  return null
}

// One required Yes/No question. A fieldset, so the legend names the radios.
function YesNo({ legend, name, value, onChange }) {
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
      <legend style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
        {legend}<span aria-hidden="true" style={{ color: '#b91c1c' }}> *</span>
      </legend>
      {[['Yes', true], ['No', false]].map(([label, v]) => (
        <label key={label} style={{ marginRight: 16, cursor: 'pointer', fontSize: 13 }}>
          <input type="radio" name={name} required checked={value === v} onChange={() => onChange(v)} style={{ marginRight: 6 }} />
          {label}
        </label>
      ))}
    </fieldset>
  )
}

// '' is "no suggestion"; anything else was checked by opFormError.
const suggestion = (value) => (value === '' || value == null ? null : Number(value))

function CreateDeal() {
  // Customer lookup
  const [customerSearch, setCustomerSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedContact, setSelectedContact] = useState(null)
  const [noResults, setNoResults] = useState(false)
  const [newCustomerEmail, setNewCustomerEmail] = useState('')
  const [creatingContact, setCreatingContact] = useState(false)

  // Deal details
  const [dealStage, setDealStage] = useState('appointmentscheduled') // Default: Quote Requested

  // Items
  const [items, setItems] = useState([{ brand: '', model: '', features: '', sp: '', bp: '', qty: '1', condition: 'Good', photos: [] }])

  // Advanced (payment details)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('') // 'paypal' or 'bank'
  const [paypalEmail, setPaypalEmail] = useState('')
  const [bankName, setBankName] = useState('')
  const [bankAccount, setBankAccount] = useState('')
  const [bankSort, setBankSort] = useState('')

  // The two OP answers (spec 20261019_op-quote-routing). null = not answered yet.
  const [itemWithUs, setItemWithUs] = useState(null)
  const [priceAgreed, setPriceAgreed] = useState(null)

  // Part Exchange
  const [isPartExchange, setIsPartExchange] = useState(false)
  const [pxSearch, setPxSearch] = useState('')
  const [pxResults, setPxResults] = useState([])
  const [pxSearching, setPxSearching] = useState(false)
  const [selectedPxItem, setSelectedPxItem] = useState(null)
  const [pxQty, setPxQty] = useState(1)

  // Pull an existing quote by deal ID (part-exchange only)
  const [useExistingDeal, setUseExistingDeal] = useState(false)
  const [existingDealId, setExistingDealId] = useState('')
  const [loadingDeal, setLoadingDeal] = useState(false)
  const [loadedDealId, setLoadedDealId] = useState(null)

  // State
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Browser draft (Trac #1104): offered back after a closed tab or a crash.
  // One draft per signed-in user. No user, no draft.
  const { user } = useContext(AuthContext) || {}
  const { draft, restoreDraft, clearDraft } = useFormAutosave(
    createDealDraftKey(user?.user_id),
    toDraftState({
      selectedContact, dealStage, items, showAdvanced, paymentMethod, paypalEmail, bankName, bankAccount, bankSort,
      itemWithUs, priceAgreed, isPartExchange, selectedPxItem, pxQty, useExistingDeal, existingDealId, loadedDealId,
    }),
    { version: CREATE_DEAL_DRAFT_VERSION },
  )

  function restoreSavedDeal() {
    const saved = restoreDraft()
    setSelectedContact(saved.selectedContact)
    setDealStage(saved.dealStage)
    setItems(saved.items.map(item => ({ ...item, photos: [] })))
    setShowAdvanced(saved.showAdvanced)
    setPaymentMethod(saved.paymentMethod)
    setItemWithUs(saved.itemWithUs)
    setPriceAgreed(saved.priceAgreed)
    setIsPartExchange(saved.isPartExchange)
    setSelectedPxItem(saved.selectedPxItem)
    setPxQty(saved.pxQty)
    setUseExistingDeal(saved.useExistingDeal)
    setExistingDealId(saved.existingDealId)
    setLoadedDealId(saved.loadedDealId)
  }

  // Search for customer
  async function searchCustomer() {
    if (!customerSearch.trim()) return

    setSearching(true)
    setError(null)
    setNoResults(false)
    try {
      const response = await api.get(`/api/deals/contacts/search?query=${encodeURIComponent(customerSearch)}`)
      setSearchResults(response.contacts || [])
      if (response.contacts?.length === 0) {
        setNoResults(true)
        // Pre-fill email if search term looks like an email
        if (customerSearch.includes('@')) {
          setNewCustomerEmail(customerSearch.trim())
        }
      }
    } catch (err) {
      setError(`Search failed: ${err.message}`)
    } finally {
      setSearching(false)
    }
  }

  // Create a new customer (Prism)
  async function createCustomer() {
    if (!newCustomerEmail.trim() || !newCustomerEmail.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    setCreatingContact(true)
    setError(null)
    try {
      const response = await api.post('/api/deals/contacts/create', { email: newCustomerEmail.trim() })
      if (response.success && response.contact) {
        selectContact(response.contact)
        setNoResults(false)
        setNewCustomerEmail('')
      } else {
        setError(response.error || 'Failed to create contact')
      }
    } catch (err) {
      setError(`Failed to create customer: ${err.message}`)
    } finally {
      setCreatingContact(false)
    }
  }

  // Select a contact
  function selectContact(contact) {
    setSelectedContact(contact)
    setSearchResults([])
    setCustomerSearch('')

    // Pre-fill payment details if available
    if (contact.paypal_email) {
      setPaymentMethod('paypal')
      setPaypalEmail(contact.paypal_email)
    } else if (contact.bank_account) {
      setPaymentMethod('bank')
      setBankName(contact.bank_name || '')
      setBankAccount(contact.bank_account || '')
      setBankSort(contact.bank_sort || '')
    }
  }

  // Add new item
  function addItem() {
    setItems([...items, { brand: '', model: '', features: '', sp: '', bp: '', qty: '1', condition: 'Good', photos: [] }])
  }

  // Remove item
  function removeItem(index) {
    if (items.length === 1) return
    setItems(items.filter((_, i) => i !== index))
  }

  // Update item field
  function updateItem(index, field, value) {
    const newItems = [...items]
    newItems[index][field] = value
    setItems(newItems)
  }

  // Search for stock item (Part Exchange)
  async function searchPxItem() {
    if (!pxSearch.trim()) return
    setPxSearching(true)
    setPxResults([])
    try {
      const response = await api.get(`/api/inventory/shopify/search?title=${encodeURIComponent(pxSearch)}&stock_status=in_stock&limit=20`)
      setPxResults(response.products || [])
    } catch (err) {
      setError(`Stock search failed: ${err.message}`)
    } finally {
      setPxSearching(false)
    }
  }

  // Map a parsed (lower-case) condition onto the form's allowed options
  function normalizeCondition(condition) {
    if (!condition) return 'Good'
    const map = {
      'mint': 'Mint',
      'excellent': 'Excellent',
      'very good': 'Very Good',
      'good': 'Good',
      'fair': 'Fair',
      'poor': 'Poor',
      'broken': 'Broken',
    }
    return map[condition.toLowerCase()] || 'Good'
  }

  // Pull an existing quote by deal ID and pre-fill items + customer (part-exchange)
  async function loadExistingDeal() {
    const dealId = existingDealId.trim()
    if (!dealId) return

    setLoadingDeal(true)
    setError(null)
    try {
      const response = await api.get(`/api/deals?dealId=${encodeURIComponent(dealId)}`)
      const deal = response.deals?.[0]
      if (!deal) {
        setError(`Deal ${dealId} not found`)
        return
      }

      // Pre-fill items from the deal's parsed_items (with the BP offered per item)
      const parsed = parseItems(deal.parsed_items).map(parseItemLine).filter(Boolean)
      if (parsed.length > 0) {
        setItems(parsed.map(p => {
          const tokens = (p.name || '').trim().split(/\s+/)
          return {
            brand: tokens[0] || '',
            model: tokens.slice(1).join(' '),
            features: p.details || '',
            sp: p.sp != null ? String(p.sp) : '',
            bp: p.bp != null ? String(p.bp) : '',
            qty: String(p.qty || 1),
            condition: normalizeCondition(p.condition),
            photos: []
          }
        }))
      }

      // Pull the customer/contact from the deal
      if (deal.seller_email) {
        try {
          const contactResp = await api.get(`/api/deals/contacts/search?query=${encodeURIComponent(deal.seller_email)}`)
          const match = contactResp.contacts?.find(c => c.email?.toLowerCase() === deal.seller_email.toLowerCase()) || contactResp.contacts?.[0]
          if (match) {
            selectContact(match)
          } else if (deal.contactId) {
            selectContact({ id: deal.contactId, email: deal.seller_email, firstname: '', lastname: '' })
          }
        } catch {
          if (deal.contactId) {
            selectContact({ id: deal.contactId, email: deal.seller_email, firstname: '', lastname: '' })
          }
        }
      }

      setLoadedDealId(deal.id)
    } catch (err) {
      setError(`Failed to load deal: ${err.message}`)
    } finally {
      setLoadingDeal(false)
    }
  }

  // Calculate totals (price * quantity per item)
  const totalSP = items.reduce((sum, item) => sum + (parseFloat(item.sp) || 0) * (parseInt(item.qty) || 1), 0)
  const totalBP = items.reduce((sum, item) => sum + (parseFloat(item.bp) || 0) * (parseInt(item.qty) || 1), 0)
  const dealAmount = totalBP

  // Hide the item editor while a deal ID is still being requested in part-ex mode
  const showItems = !(isPartExchange && useExistingDeal && !loadedDealId)

  // A plain name from the items, for the activity log and the part-exchange D1 row.
  // Prism names the quote itself (reference code and items); the old HubSpot
  // `{{{item}}}, cond, qty` format had a parser, which no longer exists.
  function generateDealName() {
    return items
      .filter(item => item.brand && item.model)
      .map(item => [item.brand, item.model, item.features].filter(Boolean).join(' '))
      .join(', ')
  }

  // Generate parsed_items format
  function generateParsedItems() {
    return items
      .filter(item => item.brand && item.model)
      .map((item, index) => {
        let line = `${index + 1}. ${item.brand} ${item.model}`
        if (item.features) line += ` - ${item.features}`
        line += ` - Qty:${item.qty || 1} - ${item.condition || 'Good'}`
        // Only a part exchange carries typed prices in the line: they become its
        // price tiers. Anything else sends them as suggestions (saveDeal).
        if (isPartExchange) line += ` - PX:Yes - SP:£${item.sp || 0} - BP:£${item.bp || 0}`
        return line
      })
      .join('\n')
  }

  // Save deal
  async function saveDeal() {
    // Validation
    if (!selectedContact) {
      setError('Please select a customer')
      return
    }

    const validItems = items.filter(item => item.brand && item.model)
    if (validItems.length === 0) {
      setError('Please add at least one item with brand and model')
      return
    }

    const priceError = isPartExchange
      ? manualPriceError(validItems)
      : opFormError(validItems, { itemWithUs, priceAgreed, isPartExchange })
    if (priceError) {
      setError(priceError)
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const dealData = {
        contactId: selectedContact.id,
        dealname: generateDealName(),
        dealstage: dealStage,
        amount: dealAmount.toString(),
        condition: validItems[0]?.condition || 'Good',
        parsed_items: generateParsedItems()
      }

      // The OP answers and the warehouse's figures, as suggestions. The engine
      // prices the quote; Quote Assessment shows these beside its figures.
      if (!isPartExchange) {
        dealData.item_with_us = itemWithUs
        dealData.price_agreed = priceAgreed
        dealData.warehouse_prices = validItems.map(item => ({ sp: suggestion(item.sp), bp: suggestion(item.bp) }))
      }

      // Add Part Exchange details if applicable
      if (isPartExchange && selectedPxItem) {
        dealData.is_part_exchange = true
        dealData.px_shopify_product_id = selectedPxItem.id || selectedPxItem.shopify_id
        dealData.px_product_title = selectedPxItem.title
        dealData.px_product_sku = selectedPxItem.sku
        dealData.px_product_price = selectedPxItem.price
        dealData.px_qty = pxQty
        dealData.px_product_image = selectedPxItem.images?.[0]?.src || null
        dealData.customer_name = selectedContact.firstname ? `${selectedContact.firstname} ${selectedContact.lastname || ''}`.trim() : ''
        dealData.customer_email = selectedContact.email || ''
      }

      // Add payment details if provided
      if (paymentMethod === 'paypal' && paypalEmail) {
        dealData.payment_paypal_email = paypalEmail
      } else if (paymentMethod === 'bank') {
        if (bankName) dealData.payment_bank_name = bankName
        if (bankAccount) dealData.payment_bank_account = bankAccount
        if (bankSort) dealData.payment_bank_sort = bankSort
      }

      const response = await api.post('/api/deals/create', dealData)

      if (response.success) {
        // Upload photos per item after deal creation
        const itemsWithPhotos = validItems.filter((item, i) => items.find(orig => orig.brand === item.brand && orig.model === item.model)?.photos?.length > 0)
        for (let i = 0; i < validItems.length; i++) {
          const origItem = items.find(orig => orig.brand === validItems[i].brand && orig.model === validItems[i].model)
          if (origItem?.photos?.length > 0) {
            for (const photo of origItem.photos) {
              try {
                const photoForm = new FormData()
                photoForm.append('file', photo)
                photoForm.append('itemIndex', String(i))
                await fetch(`/api/deals/${response.dealId}/images`, {
                  method: 'POST',
                  credentials: 'include',
                  body: photoForm
                })
              } catch (photoErr) {
                console.error('Photo upload failed:', photoErr)
              }
            }
          }
        }

        // The reference code is what an operator quotes back to a customer and
        // searches on; the raw quote UUID is neither. Falling back to it only
        // when there is genuinely no code to show.
        const ref = response.referenceCode || response.dealId
        if (response.pricingError) {
          // The quote exists — saying "failed" here would earn a duplicate.
          setError(`Deal ${ref} was created, but the prices were NOT saved: ${response.pricingError}`)
        }
        setSuccess(`Deal created successfully! Reference: ${ref}`)
        clearDraft()
        // Reset form
        setSelectedContact(null)
        setItems([{ brand: '', model: '', features: '', sp: '', bp: '', qty: '1', condition: 'Good', photos: [] }])
        setDealStage('appointmentscheduled')
        setPaymentMethod('')
        setPaypalEmail('')
        setBankName('')
        setBankAccount('')
        setBankSort('')
        setShowAdvanced(false)
        setItemWithUs(null)
        setPriceAgreed(null)
        setIsPartExchange(false)
        setSelectedPxItem(null)
        setPxSearch('')
        setPxResults([])
        setPxQty(1)
        setUseExistingDeal(false)
        setExistingDealId('')
        setLoadedDealId(null)
      } else {
        setError(response.error || 'Failed to create deal')
      }
    } catch (err) {
      setError(`Failed to create deal: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="pageBody" style={{ paddingTop: 24 }}>
      <div className="card" style={{ maxWidth: 900, margin: '0 auto' }}>
        <h2 style={{ marginTop: 0, marginBottom: 24 }}>Create New Deal</h2>

        {/* Error/Success Messages */}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c' }}>
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: 16, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#15803d' }}>
            {success}
          </div>
        )}

        {draft && (
          <div role="status" data-testid="create-deal-draft-banner" style={{ marginBottom: 16, padding: '12px 16px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              You have an unsaved deal from {formatDayMonthTime(new Date(draft.savedAt))}.
              {' '}Photos and payout details are not kept.
            </div>
            <button className="btn btn-sm btn-primary" onClick={restoreSavedDeal}>Restore it</button>
            <button className="btn btn-sm btn-secondary" onClick={clearDraft}>Discard</button>
          </div>
        )}

        {/* Customer Lookup */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Customer</label>

          {selectedContact ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: 'var(--bg)', borderRadius: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>
                  {selectedContact.firstname} {selectedContact.lastname}
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>{selectedContact.email}</div>
              </div>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedContact(null)}
              >
                Change
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search by name or email..."
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchCustomer()}
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-primary"
                onClick={searchCustomer}
                disabled={searching || !customerSearch.trim()}
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div style={{ marginTop: 8, border: '1px solid #2563eb', borderRadius: 8, maxHeight: 200, overflow: 'auto', background: '#eff6ff' }}>
              <div style={{ padding: '8px 12px', background: '#2563eb', color: 'white', fontSize: 12, fontWeight: 600 }}>
                Click to select a customer ({searchResults.length} found)
              </div>
              {searchResults.map(contact => (
                <div
                  key={contact.id}
                  style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid #bfdbfe',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    background: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onClick={() => selectContact(contact)}
                  onMouseOver={e => e.currentTarget.style.background = '#dbeafe'}
                  onMouseOut={e => e.currentTarget.style.background = 'white'}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{contact.firstname} {contact.lastname}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{contact.email}</div>
                  </div>
                  <span style={{ color: '#2563eb', fontSize: 12, fontWeight: 500 }}>Select →</span>
                </div>
              ))}
            </div>
          )}

          {/* Create Customer - shown when no results */}
          {noResults && !selectedContact && (
            <div style={{ marginTop: 8, border: '1px solid #f59e0b', borderRadius: 8, background: '#fffbeb' }}>
              <div style={{ padding: '8px 12px', background: '#f59e0b', color: 'white', fontSize: 12, fontWeight: 600 }}>
                No contacts found — create a new customer
              </div>
              <div style={{ padding: 12 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="customer@email.com"
                    value={newCustomerEmail}
                    onChange={e => setNewCustomerEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && createCustomer()}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={createCustomer}
                    disabled={creatingContact || !newCustomerEmail.includes('@')}
                  >
                    {creatingContact ? 'Creating...' : 'Create Customer'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#92400e', marginTop: 6 }}>
                  A new customer will be created using this email address.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Deal Stage */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Deal Stage</label>
          <select
            className="form-input"
            value={dealStage}
            onChange={e => setDealStage(e.target.value)}
            style={{ maxWidth: 300 }}
          >
            {NEW_DEAL_STAGES.map(stage => (
              <option key={stage.id} value={stage.id}>{stage.label}</option>
            ))}
          </select>
        </div>

        {/* Items */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <label style={{ fontWeight: 600 }}>{isPartExchange && useExistingDeal ? 'Deal Information' : 'Items'}</label>
            {showItems && (
              <button className="btn btn-sm btn-secondary" onClick={addItem}>
                + Add Item
              </button>
            )}
          </div>

          {/* The two OP answers decide what Quote Ready does (spec
              20261019_op-quote-routing). Not asked of a part exchange. */}
          {!isPartExchange && (
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 12 }}>
              <YesNo legend="Is the item with us?" name="item-with-us" value={itemWithUs} onChange={setItemWithUs} />
              <YesNo legend="Is the price agreed with the customer?" name="price-agreed" value={priceAgreed} onChange={setPriceAgreed} />
            </div>
          )}

          {/* A part exchange's typed prices become its price tiers, so the engine
              skips it. Any other OP quote is priced by the engine, and what is
              typed here is shown beside the engine's figures. */}
          {showItems && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
              {isPartExchange
                ? 'Leave SP/BP blank to have the pricing engine price this quote. Typed prices are kept as they are, and the engine skips the quote. Price all items or none.'
                : "The pricing engine prices every quote. Your figures show next to the engine's on Quote Assessment. If the price is agreed, enter the agreed BP for one unit of every item. It is final."}
            </div>
          )}

          {/* Part-exchange: pull an existing quote instead of typing a new one */}
          {isPartExchange && (
            <div style={{ display: 'flex', gap: 20, marginBottom: 12, padding: '10px 12px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="radio"
                  name="dealInfoMode"
                  checked={!useExistingDeal}
                  onChange={() => { setUseExistingDeal(false); setLoadedDealId(null); setExistingDealId('') }}
                />
                Enter new quote details
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="radio"
                  name="dealInfoMode"
                  checked={useExistingDeal}
                  onChange={() => setUseExistingDeal(true)}
                />
                Enter existing deal ID
              </label>
            </div>
          )}

          {/* Existing deal ID lookup */}
          {isPartExchange && useExistingDeal && (
            <div style={{ marginBottom: 16 }}>
              {loadedDealId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                  <div style={{ flex: 1, fontSize: 14, color: '#15803d' }}>
                    Loaded quote from <strong>Deal #{loadedDealId}</strong> — review the items below.
                  </div>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => { setLoadedDealId(null); setExistingDealId('') }}
                  >
                    Load different deal
                  </button>
                </div>
              ) : (
                <>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Existing Deal ID</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 12345678901"
                      value={existingDealId}
                      onChange={e => setExistingDealId(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && loadExistingDeal()}
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={loadExistingDeal}
                      disabled={loadingDeal || !existingDealId.trim()}
                    >
                      {loadingDeal ? 'Loading...' : 'Load Quote'}
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: '#92400e', marginTop: 6 }}>
                    Pulls the parsed items (with BP offered) and customer from the existing deal.
                  </div>
                </>
              )}
            </div>
          )}

          {showItems && items.map((item, index) => (
            <div key={index} style={{
              padding: 16,
              background: 'var(--bg)',
              borderRadius: 8,
              marginBottom: 12,
              position: 'relative'
            }}>
              {items.length > 1 && (
                <button
                  onClick={() => removeItem(index)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'none',
                    border: 'none',
                    color: 'var(--muted)',
                    cursor: 'pointer',
                    fontSize: 18
                  }}
                  title="Remove item"
                >
                  &times;
                </button>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Brand *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Fender, Roland, Shure"
                    value={item.brand}
                    onChange={e => updateItem(index, 'brand', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Model *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Stratocaster, TD-17, SM58"
                    value={item.model}
                    onChange={e => updateItem(index, 'model', e.target.value)}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Features (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Electric Guitar - Sunburst - 2019"
                  value={item.features}
                  onChange={e => updateItem(index, 'features', e.target.value)}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Condition</label>
                  <select
                    className="form-input"
                    value={item.condition}
                    onChange={e => updateItem(index, 'condition', e.target.value)}
                  >
                    <option value="Mint">Like New</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Very Good">Very Good</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Broken">Spares or Repair</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Qty</label>
                  <input
                    type="number"
                    className="form-input"
                    min="1"
                    value={item.qty}
                    onChange={e => updateItem(index, 'qty', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                    {isPartExchange ? 'SP' : priceAgreed ? 'Agreed SP each' : 'Your SP each (suggestion)'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }}>£</span>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0.00"
                      value={item.sp}
                      onChange={e => updateItem(index, 'sp', e.target.value)}
                      style={{ paddingLeft: 28 }}
                    />
                  </div>
                </div>
                <div>
                  {/* No asterisk: BP is optional unless the price is agreed (opFormError). */}
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                    {isPartExchange ? 'BP' : priceAgreed ? 'Agreed BP each' : 'Your BP each (suggestion)'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }}>£</span>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0.00"
                      value={item.bp}
                      onChange={e => updateItem(index, 'bp', e.target.value)}
                      style={{ paddingLeft: 28 }}
                    />
                  </div>
                  {/* Prism multiplies the BP by Qty, so say what the line pays. */}
                  {Number(item.qty) > 1 && Number(item.bp) > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                      {`£${String(Math.round(Number(item.bp) * Number(item.qty) * 100) / 100)} for ${Number(item.qty)}`}
                    </div>
                  )}
                </div>
              </div>

              {/* Per-item photos */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label
                    className="btn btn-sm btn-secondary"
                    style={{ cursor: 'pointer', margin: 0, fontSize: 12 }}
                  >
                    Add Photos
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={e => {
                        const newPhotos = [...(item.photos || []), ...Array.from(e.target.files)]
                        updateItem(index, 'photos', newPhotos)
                        e.target.value = ''
                      }}
                    />
                  </label>
                  {item.photos?.length > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{item.photos.length} photo{item.photos.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
                {item.photos?.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {item.photos.map((photo, pi) => (
                      <div key={pi} style={{ position: 'relative', width: 64, height: 64 }}>
                        <img
                          src={URL.createObjectURL(photo)}
                          alt=""
                          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }}
                        />
                        <button
                          onClick={() => {
                            const updated = item.photos.filter((_, j) => j !== pi)
                            updateItem(index, 'photos', updated)
                          }}
                          style={{
                            position: 'absolute', top: -6, right: -6,
                            width: 18, height: 18, borderRadius: '50%',
                            background: '#EF4444', color: 'white', border: 'none',
                            cursor: 'pointer', fontSize: 11, lineHeight: '18px',
                            textAlign: 'center', padding: 0
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Second Add Item button, directly under the list, so a long item
              list doesn't force a scroll back up to the header button. Same
              handler and styling as the top one. */}
          {showItems && items.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <button className="btn btn-sm btn-secondary" onClick={addItem}>
                + Add Item
              </button>
            </div>
          )}

          {/* Totals */}
          {showItems && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, padding: '12px 16px', background: '#f0f9ff', borderRadius: 8, fontSize: 14 }}>
              <div>Total SP: <strong>£{totalSP.toFixed(2)}</strong></div>
              <div>Total BP: <strong>£{totalBP.toFixed(2)}</strong></div>
              <div style={{ color: '#2563eb', fontWeight: 600 }}>
                Deal Amount: £{dealAmount.toFixed(2)}
              </div>
            </div>
          )}
        </div>

        {/* Part Exchange Section */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={isPartExchange}
              onChange={e => {
                setIsPartExchange(e.target.checked)
                if (!e.target.checked) {
                  setSelectedPxItem(null)
                  setPxSearch('')
                  setPxResults([])
                  setPxQty(1)
                  setUseExistingDeal(false)
                  setExistingDealId('')
                  setLoadedDealId(null)
                }
              }}
              style={{ width: 18, height: 18 }}
            />
            Part Exchange / Trade-In
          </label>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, marginLeft: 28 }}>
            Customer trades in their item(s) against a stock item they want to purchase
          </div>

          {isPartExchange && (
            <div style={{ marginTop: 16, padding: 16, background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Stock Item (Customer Wants)</label>

              {selectedPxItem ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  {selectedPxItem.images?.[0]?.src && (
                    <img
                      src={selectedPxItem.images[0].src}
                      alt=""
                      style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb' }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedPxItem.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', gap: 12, marginTop: 2 }}>
                      {selectedPxItem.sku && <span>SKU: {selectedPxItem.sku}</span>}
                      <span style={{ color: '#2563eb', fontWeight: 600 }}>£{parseFloat(selectedPxItem.price || 0).toFixed(2)} ea</span>
                      <span>Stock: {selectedPxItem.quantity}</span>
                    </div>
                  </div>
                  {selectedPxItem.quantity > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <label style={{ fontSize: 12, fontWeight: 500 }}>Qty:</label>
                      <input
                        type="number"
                        className="form-input"
                        min="1"
                        max={selectedPxItem.quantity}
                        value={pxQty}
                        onChange={e => setPxQty(Math.max(1, Math.min(parseInt(e.target.value) || 1, selectedPxItem.quantity)))}
                        style={{ width: 60, padding: '4px 8px', textAlign: 'center' }}
                      />
                    </div>
                  )}
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => { setSelectedPxItem(null); setPxSearch(''); setPxResults([]); setPxQty(1); }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search by product title..."
                    value={pxSearch}
                    onChange={e => setPxSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && searchPxItem()}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={searchPxItem}
                    disabled={pxSearching || !pxSearch.trim()}
                  >
                    {pxSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              )}

              {/* PX Search Results */}
              {pxResults.length > 0 && !selectedPxItem && (
                <div style={{ marginTop: 8, border: '1px solid #f59e0b', borderRadius: 8, maxHeight: 300, overflow: 'auto', background: 'white' }}>
                  <div style={{ padding: '8px 12px', background: '#f59e0b', color: 'white', fontSize: 12, fontWeight: 600 }}>
                    Click to select ({pxResults.length} result{pxResults.length !== 1 ? 's' : ''})
                  </div>
                  {pxResults.map(product => (
                    <div
                      key={product.id || product.shopify_id}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #fef3c7',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        background: 'white'
                      }}
                      onClick={() => { setSelectedPxItem(product); setPxResults([]); }}
                      onMouseOver={e => e.currentTarget.style.background = '#fffbeb'}
                      onMouseOut={e => e.currentTarget.style.background = 'white'}
                    >
                      {product.images?.[0]?.src && (
                        <img src={product.images[0].src} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{product.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {product.sku && `SKU: ${product.sku} · `}
                          {product.vendor && `${product.vendor} · `}
                          Stock: {product.quantity}
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, color: '#2563eb', fontSize: 14 }}>£{parseFloat(product.price || 0).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Balance Calculation */}
              {selectedPxItem && totalBP > 0 && (() => {
                const pxTotal = parseFloat(selectedPxItem.price || 0) * pxQty
                const balance = pxTotal - totalBP
                return (
                <div style={{ marginTop: 16, padding: 16, background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase' }}>
                    Part Exchange Calculation
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14 }}>
                    <span>Stock Item ({selectedPxItem.title.length > 30 ? selectedPxItem.title.substring(0, 30) + '...' : selectedPxItem.title}){pxQty > 1 ? ` x${pxQty}` : ''}</span>
                    <span style={{ fontWeight: 600 }}>£{pxTotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: '#16a34a' }}>
                    <span>Trade-In Value (Total BP)</span>
                    <span style={{ fontWeight: 600 }}>-£{totalBP.toFixed(2)}</span>
                  </div>
                  <div style={{ borderTop: '2px solid #1e293b', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700 }}>
                    <span>Balance to Pay</span>
                    <span style={{ color: balance > 0 ? '#1e293b' : '#16a34a' }}>
                      £{balance.toFixed(2)}
                    </span>
                  </div>
                </div>
                )
              })()}
            </div>
          )}
        </div>

        {/* Advanced Section - Payment Details */}
        <div style={{ marginBottom: 24 }}>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontWeight: 600,
              color: 'var(--text)'
            }}
          >
            <span style={{
              transform: showAdvanced ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              display: 'inline-block'
            }}>
              &#9654;
            </span>
            Advanced - Payment Details
          </button>

          {showAdvanced && (
            <div style={{ marginTop: 16, padding: 16, background: 'var(--bg)', borderRadius: 8 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Payment Method</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={paymentMethod === 'paypal'}
                      onChange={() => setPaymentMethod('paypal')}
                    />
                    PayPal
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={paymentMethod === 'bank'}
                      onChange={() => setPaymentMethod('bank')}
                    />
                    Bank Transfer
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={paymentMethod === ''}
                      onChange={() => setPaymentMethod('')}
                    />
                    Not specified
                  </label>
                </div>
              </div>

              {paymentMethod === 'paypal' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>PayPal Email</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="customer@email.com"
                    value={paypalEmail}
                    onChange={e => setPaypalEmail(e.target.value)}
                  />
                </div>
              )}

              {paymentMethod === 'bank' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Account Name</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="John Smith"
                      value={bankName}
                      onChange={e => setBankName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Account Number</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="12345678"
                      value={bankAccount}
                      onChange={e => setBankAccount(e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Sort Code</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="12-34-56"
                      value={bankSort}
                      onChange={e => setBankSort(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* The same error again, where the click was: the banner at the top is
            often off-screen by now, and a refused save looked like a dead button. */}
        {error && (
          <div role="alert" data-testid="create-deal-error" style={{ marginBottom: 12, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontWeight: 600 }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
          {!selectedContact && (
            <span style={{ fontSize: 12, color: '#b91c1c' }}>Select a customer first</span>
          )}
          {selectedContact && !items.some(item => item.brand && item.model) && (
            <span style={{ fontSize: 12, color: '#b91c1c' }}>Add at least one item with brand and model</span>
          )}
          <button
            className="btn btn-primary"
            onClick={saveDeal}
            disabled={saving || !selectedContact || !items.some(item => item.brand && item.model)}
          >
            {saving ? 'Creating Deal...' : 'Create Deal'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateDeal
